<template>
  <div
    class="acct-table-shell"
    :class="categoryToneClass.replace('acct-table-meta__category', 'acct-table-shell')">
    <div
      v-if="projectName || categoryTitle"
      class="acct-table-meta">
      <div class="acct-table-meta__left">
        <div class="d-flex align-center">
          <div
            v-if="categoryTitle"
            class="acct-table-meta__category text-capitalize mr-2"
            :class="categoryToneClass">
            {{ categoryTitle }}
          </div>
        </div>
        <span v-if="projectName" class="acct-table-meta__project">{{ projectName }}</span>

        <v-tooltip v-if="showExport" top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              elevation="0"
              class="acct-btn-export"
              :loading="exportLoading"
              :disabled="exportLoading"
              v-bind="attrs"
              v-on="on"
              aria-label="Export Excel"
              @click="$emit('export')">
              <v-icon size="16">mdi-microsoft-excel</v-icon>
            </v-btn>
          </template>
          <span>Export Excel</span>
        </v-tooltip>
      </div>
      <div class="acct-table-meta__actions">
        <div
          class="acct-table-meta__right"
          :class="isDeficit ? 'acct-table-meta__right--deficit' : ''">
          <span
            class="acct-table-meta__balance-label"
            :class="isDeficit ? 'acct-table-meta__balance-label--deficit' : ''">
            {{ isDeficit ? 'Defisit' : 'Saldo' }}
          </span>
          <span
            class="acct-table-meta__balance-value"
            :class="isDeficit ? 'acct-table-meta__balance-value--deficit' : ''">
            <template v-if="isDeficit">− </template>{{ balance | price }}
          </span>
        </div>
      </div>
    </div>

    <div class="acct-table-scroll">
      <table class="acct-table acct-table--realisasi">
        <colgroup>
          <col style="width: 44px">
          <col style="width: 150px">
          <col style="width: 170px">
          <col>
          <col style="width: 120px">
          <col style="width: 180px">
        </colgroup>
        <thead class="acct-table__head">
          <tr>
            <th scope="col" class="acct-table__th--center">Aksi</th>
            <th scope="col" class="acct-table__th--left">Waktu</th>
            <th scope="col" class="acct-table__th--left">No bukti</th>
            <th scope="col" class="acct-table__th--left">Keterangan</th>
            <th scope="col" class="acct-table__th--right">Nominal</th>
            <th scope="col" class="acct-table__th--left">Diinput</th>
          </tr>
        </thead>
        <tbody class="acct-table__body">
          <tr
            v-for="(item, index) in items"
            :key="index"
            class="acct-table__row"
            :class="item.ledger_number ? 'acct-table__row--interactive' : ''">
            <td class="acct-table__cell acct-table__cell--action">
              <v-tooltip v-if="item.ledger_number" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    class="acct-btn-jurnal"
                    v-bind="attrs"
                    v-on="on"
                    :aria-label="'Lihat jurnal ' + item.ledger_number"
                    @click.stop="$emit('row-click', item)">
                    <v-icon size="16">mdi-book-open-outline</v-icon>
                  </v-btn>
                </template>
                <span>Lihat jurnal</span>
              </v-tooltip>
              <span v-else class="acct-table__muted">—</span>
            </td>
            <td class="acct-table__cell acct-table__cell--muted">{{ formatShortDatetime(item.input_datetime) }}</td>
            <td
              class="acct-table__cell acct-table__cell--mono acct-table__cell--left"
              :title="item.ledger_number || ''">
              <span style="font-size: 12px;">{{ item.ledger_number }}</span>
            </td>
            <td class="acct-table__cell acct-table__cell--left">
              <div
                v-if="item.note"
                class=""
                style="font-size: 12px;"
                :title="item.note">
                {{ item.note }}
              </div>
              <span v-else class="acct-table__muted">—</span>
            </td>
            <td class="acct-table__cell acct-table__cell--amount acct-table__cell--mono" style="font-size: 12px;">{{ item.value | price }}</td>
            <td class="acct-table__cell acct-table__cell--left">
              <div
                v-if="item.input_admin_name"
                class=""
                style="font-size: 12px;"
                :title="item.input_admin_name">
                {{ item.input_admin_name }}
              </div>
              <span v-else class="acct-table__muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ProjectLedgerPagination
      v-if="pagination && pagination.total_data > 0"
      :model="pagination"
      :display="paginationDisplay"
      @change-page="$emit('change-page', $event)"
      @reload-page="$emit('reload-page', $event)" />
  </div>
</template>

<script>
import moment from 'moment'
import 'moment/locale/id'
import ProjectLedgerPagination from '@/components/project/ProjectLedgerPagination.vue'

export default {
  name: 'ProjectRealisasiTable',
  components: { ProjectLedgerPagination },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    categoryTitle: {
      type: String,
      default: ''
    },
    projectName: {
      type: String,
      default: ''
    },
    balance: {
      type: [Number, String],
      default: 0
    },
    pagination: {
      type: Object,
      default: null
    },
    paginationDisplay: {
      type: Object,
      default: () => ({ page: 1, row: 20 })
    },
    showExport: {
      type: Boolean,
      default: false
    },
    exportLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isDeficit () {
      return Number(this.balance) < 0
    },
    categoryToneClass () {
      const t = (this.categoryTitle || '').toLowerCase()
      if (t.includes('belanja')) return 'acct-table-meta__category--expense'
      if (t.includes('pendapatan') || t.includes('piutang')) return 'acct-table-meta__category--income'
      if (t.includes('transfer')) return 'acct-table-meta__category--transfer'
      return 'acct-table-meta__category--default'
    }
  },
  methods: {
    formatShortDatetime (value) {
      if (!value) return '—'
      moment.locale('id')
      return moment(value).format('DD MMM YYYY HH:mm')
    },
    shortLedgerNumber (value) {
      if (!value) return '—'
      const parts = String(value).split('-')
      if (parts.length >= 2) {
        return '...-' + parts[parts.length - 1]
      }
      return value.length > 10 ? '...' + value.slice(-8) : value
    }
  }
}
</script>

<style scoped>
.acct-table-shell {
  --acct-bg: #ffffff;
  --acct-surface: #f8fafc;
  --acct-head: #e9edf2;
  --acct-border: #e0e3e8;
  --acct-text: #1f2933;
  --acct-muted: #4b5563;
  --acct-head-text: #1f2933;
  --acct-hover: #e8f2f8;
  --acct-stripe: #f3f6fa;
  --acct-income: #0072B2;
  --acct-expense: #D55E00;
  --acct-transfer: #E69F00;
  --acct-default: #56B4E9;
  --acct-deficit: #7B4B9A;
  --acct-accent: var(--acct-default);
  --acct-radius: 8px;

  border: 1px solid var(--acct-border);
  border-radius: var(--acct-radius);
  overflow: hidden;
  background: var(--acct-bg);
  color: var(--acct-text);
}

.acct-table-shell--income { --acct-accent: var(--acct-income); }
.acct-table-shell--expense { --acct-accent: var(--acct-expense); }
.acct-table-shell--transfer { --acct-accent: var(--acct-transfer); }
.acct-table-shell--default { --acct-accent: var(--acct-default); }

.acct-table-meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  background: var(--acct-surface);
  border-bottom: 1px solid var(--acct-border);
}

.acct-table-meta__left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.acct-table-meta__category {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 1.3;
}
.acct-table-meta__category::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.acct-table-meta__category--income {
  color: var(--acct-income);
}
.acct-table-meta__category--expense {
  color: var(--acct-expense);
}
.acct-table-meta__category--transfer {
  color: var(--acct-transfer);
}
.acct-table-meta__category--default {
  color: var(--acct-muted);
}

.acct-table-meta__project {
  font-size: 14px;
  font-weight: 600;
  color: var(--acct-text);
  line-height: 1.4;
  word-break: break-word;
}

.acct-table-meta__actions {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
}

.acct-btn-export {
  background-color: #e6f5f0 !important;
  border: 1px solid #7dccb8 !important;
  color: #009E73 !important;
  min-width: 28px !important;
  width: 28px !important;
  height: 28px !important;
  box-shadow: none !important;
  border-radius: 6px !important;
  margin-top: 2px;
}
.acct-btn-export::before {
  opacity: 0 !important;
}
.acct-btn-export:hover {
  background-color: #cceee3 !important;
  border-color: #009E73 !important;
}
.acct-btn-export .v-icon {
  color: #009E73 !important;
}

.acct-table-meta__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  background: rgba(255, 255, 255);
  border: 1px solid var(--acct-border);
  border-radius: 6px;
  padding: 8px 12px;
  min-width: 120px;
}
/* .acct-table-shell--income .acct-table-meta__right {
  background: rgba(0, 114, 178, 0.07);
}
.acct-table-shell--expense .acct-table-meta__right {
  background: rgba(213, 94, 0, 0.07);
}
.acct-table-shell--transfer .acct-table-meta__right {
  background: rgba(230, 159, 0, 0.08);
}
.acct-table-shell--default .acct-table-meta__right {
  background: rgba(86, 180, 233, 0.06);
}
.acct-table-meta__right--deficit {
  background: rgba(123, 75, 154, 0.07);
} */

.acct-table-meta__balance-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--acct-muted);
}
.acct-table-meta__balance-label--deficit {
  color: var(--acct-deficit);
}

.acct-table-meta__balance-value {
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--acct-text);
}
.acct-table-meta__balance-value--deficit {
  color: var(--acct-text);
}

.acct-table-scroll {
  overflow: auto;
  max-height: min(55vh, calc(100vh - 320px));
}

.acct-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
}

.acct-table__head th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--acct-head);
  color: var(--acct-head-text);
  font-size: 11px;
  font-weight: 700;
  padding: 10px 12px;
  text-align: left;
  white-space: nowrap;
  border-bottom: 2px solid var(--acct-border);
  letter-spacing: 0.02em;
}

.acct-table__th--left { text-align: left; }
.acct-table__th--right { text-align: right; }
.acct-table__th--center { text-align: center; }

.acct-table__cell--action {
  text-align: center;
  padding: 6px 4px;
}

/* Okabe-Ito blue — sama pola lr-filter-btn--compare di laba-rugi */
.acct-btn-jurnal {
  background-color: #e8f2f8 !important;
  border: 1px solid #9ec5de !important;
  color: #0072b2 !important;
  min-width: 28px !important;
  width: 28px !important;
  height: 28px !important;
  box-shadow: none !important;
  border-radius: 4px !important;
}
.acct-btn-jurnal::before {
  opacity: 0 !important;
}
.acct-btn-jurnal:hover {
  background-color: #d4e8f4 !important;
  border-color: #0072b2 !important;
  box-shadow: 0 0 0 2px rgba(86, 180, 233, 0.2) !important;
}
.acct-btn-jurnal .v-icon {
  color: #0072b2 !important;
}

.acct-table__body td {
  padding: 9px 12px;
  border-bottom: 1px solid var(--acct-border);
  vertical-align: middle;
}

.acct-table__body tr:nth-child(even) td {
  background: var(--acct-stripe);
}

.acct-table__row--interactive {
  cursor: default;
}

.acct-table__row--interactive:hover td {
  background: var(--acct-hover) !important;
}
.acct-table__row--interactive:hover .acct-btn-jurnal {
  background-color: #d4e8f4 !important;
}

.acct-table__cell--left { text-align: left; }
.acct-table__cell--muted { color: var(--acct-muted); font-size: 12px; }
.acct-table__cell--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.acct-table__cell--amount {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--acct-accent);
}

.acct-table__muted { color: var(--acct-muted); }

.acct-ellipsis {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}
.acct-ellipsis--1 { -webkit-line-clamp: 1; }

@media (max-width: 1264px) {
  .acct-table-scroll { max-height: calc(100vh - 300px); }
  .acct-table-meta { flex-direction: column; }
  .acct-table-meta__actions {
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }
  .acct-table-meta__right { align-items: flex-start; }
}
</style>
